import { answerFromField, BOOLEAN_FIELD_OPTIONS, displayAnsweredValue } from "@repo/data-store";
import {
  answeredValueProblem,
  asMemberId,
  asPlannedTrialId,
  fulfilledPlannedTrials,
  type ParameterAnswer,
  type ParameterValue,
  type ProcedureTrial,
  type ProcedureTrialValue,
  quantityText,
} from "@repo/domain";
import {
  declaredValue,
  type PresentedOrderSetOrderDto,
  type PresentedOrderSetParameterDto,
} from "@/lib/order-set-presentation";

export type TrialFieldValue = string | string[] | number | undefined;
export const trialParameterKey = (parameter: { source: string; parameterId: string }) =>
  `${parameter.source}:${parameter.parameterId}`;

function trialDeclaration(parameter: PresentedOrderSetParameterDto): ParameterValue {
  const value = declaredValue(parameter.value);
  return value.kind === "Choice"
    ? { ...value, options: value.options.map((option) => ({ id: asMemberId(option.id), label: option.label })) }
    : value;
}

export function orderedTrialValues(order: Pick<PresentedOrderSetOrderDto, "parameterValues">): ProcedureTrialValue[] {
  return order.parameterValues.map((answer) => ({
    source: "ordered",
    answer: {
      source: answer.source === "self" ? "self" : asMemberId(answer.source),
      parameterId: asMemberId(answer.parameterId),
      value:
        answer.value.kind === "Choice"
          ? { ...answer.value, selected: answer.value.selected.map(asMemberId) }
          : answer.value,
    },
  }));
}

/** "As ordered" for the planned trial at `position`, else the order's own answers. */
export function plannedTrialValues(
  order: Pick<PresentedOrderSetOrderDto, "parameterValues" | "plannedTrials">,
  position: number,
): ProcedureTrialValue[] {
  return orderedTrialValues({ parameterValues: order.plannedTrials?.[position]?.values ?? order.parameterValues });
}

/** The cell editor's inputs for one parameter: an option list for choices and yes/no, typed text otherwise. */
export function trialEditorProps(parameter: PresentedOrderSetParameterDto, answer?: ParameterAnswer) {
  const definition = parameter.value;
  const value = answer?.value;
  switch (definition.kind) {
    case "Choice":
      return {
        options: definition.options.map((option) => ({ value: option.optionId, label: option.label })),
        multiple: definition.cardinality === "Multiple",
        freeText: definition.allowFreeText ?? false,
        selected: value?.kind === "Choice" ? value.selected.map(String) : [],
        text: value?.kind === "Choice" ? (value.freeText ?? "") : "",
      };
    case "Boolean":
      return {
        options: BOOLEAN_FIELD_OPTIONS,
        selected: value?.kind === "Boolean" ? [String(value.value)] : [],
      };
    case "Text":
      return { text: value?.kind === "Text" ? value.value : "" };
    default: {
      const quantity = quantityText(definition);
      return {
        text: value && "value" in value && typeof value.value === "number" ? quantity.format(value.value) : "",
        ...(quantity.suffix === undefined ? {} : { suffix: quantity.suffix }),
        inputMode: definition.kind === "Integer" ? ("numeric" as const) : ("decimal" as const),
      };
    }
  }
}

/** The answer a committed cell editor holds: undefined clears it, false means the text is not a number. */
export function trialEditorAnswer(
  parameter: PresentedOrderSetParameterDto,
  edited: { selected: readonly string[]; text: string },
): ParameterAnswer | undefined | false {
  const definition = parameter.value;
  switch (definition.kind) {
    case "Choice":
      return trialAnswer(
        parameter,
        definition.cardinality === "Multiple" ? [...edited.selected] : (edited.selected[0] ?? ""),
        edited.text,
      );
    case "Boolean":
      return trialAnswer(parameter, edited.selected[0] ?? "", "");
    case "Text":
      return trialAnswer(parameter, edited.text, "");
    default: {
      const unit = definition.kind === "Quantity" ? definition.unit?.display : undefined;
      let text = edited.text.trim();
      if (unit && text.endsWith(unit)) text = text.slice(0, -unit.length).trim();
      if (!text) return undefined;
      const number = quantityText(definition).parse(text);
      return number === undefined ? false : trialAnswer(parameter, number, "");
    }
  }
}

export const formatTrialTime = (date: Date) => date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

/** A typed clock time ("15:30", "3:30 pm", "9a") on the same day as `base`. */
export function parseTrialTime(text: string, base: Date): Date | undefined {
  const match = /^(\d{1,2})(?::(\d{2}))?\s*(?:([ap])\.?m?\.?)?$/i.exec(text.trim());
  if (!match) return undefined;
  let hours = Number(match[1]);
  const minutes = Number(match[2] ?? 0);
  const meridiem = match[3]?.toLowerCase();
  if (meridiem && (hours < 1 || hours > 12)) return undefined;
  if (meridiem === "p" && hours < 12) hours += 12;
  if (meridiem === "a" && hours === 12) hours = 0;
  if (hours > 23 || minutes > 59) return undefined;
  const date = new Date(base);
  date.setHours(hours, minutes, 0, 0);
  return date;
}

export function trialAnswer(
  parameter: PresentedOrderSetParameterDto,
  raw: TrialFieldValue,
  freeText: TrialFieldValue,
): ParameterAnswer | undefined {
  const value = answerFromField(trialDeclaration(parameter), raw, freeText);
  return value
    ? {
        source: parameter.source === "self" ? "self" : asMemberId(parameter.source),
        parameterId: asMemberId(parameter.parameterId),
        value,
      }
    : undefined;
}

export function trialProblem(
  parameters: readonly PresentedOrderSetParameterDto[],
  values: readonly ProcedureTrialValue[],
): string | undefined {
  for (const parameter of parameters) {
    const answer = values.find((value) => trialParameterKey(value.answer) === trialParameterKey(parameter))?.answer;
    if (!answer) continue;
    const problem = answeredValueProblem(trialDeclaration(parameter), answer.value);
    if (problem) return `${parameter.label} ${problem}`;
  }
  return undefined;
}

export function displayTrialAnswer(
  answer: ParameterAnswer,
  parameters: readonly PresentedOrderSetParameterDto[],
): string {
  const parameter = parameters.find((candidate) => trialParameterKey(candidate) === trialParameterKey(answer));
  return displayAnsweredValue(parameter && trialDeclaration(parameter), answer.value);
}

/** Clipboard input uses the same definition validation as a manually entered value. */
export function parseTrialClipboard(
  parameter: PresentedOrderSetParameterDto,
  text: string,
): ProcedureTrialValue | null | undefined {
  let raw: TrialFieldValue = text.trim();
  if (!raw) return null;
  const definition = parameter.value;
  switch (definition.kind) {
    case "Quantity":
    case "Integer": {
      const unit = definition.kind === "Quantity" ? definition.unit?.display : undefined;
      if (unit && raw.endsWith(` ${unit}`)) raw = raw.slice(0, -unit.length - 1);
      raw = quantityText(definition).parse(raw);
      break;
    }
    case "Boolean":
      raw = raw.toLowerCase() === "yes" ? "true" : raw.toLowerCase() === "no" ? "false" : raw;
      break;
    case "Choice": {
      const labels = definition.cardinality === "Multiple" ? raw.split(", ") : [raw];
      const options = labels.map(
        (label) => definition.options.find((option) => option.label.toLowerCase() === label.toLowerCase())?.optionId,
      );
      if (options.some((option) => option === undefined)) return undefined;
      raw = options.filter((option): option is string => option !== undefined);
      break;
    }
  }
  const answer = trialAnswer(parameter, raw, "");
  const value: ProcedureTrialValue | undefined = answer ? { answer, source: "entered" } : undefined;
  return value && !trialProblem([parameter], [value]) ? value : undefined;
}

export function trialFulfillment(
  recorded: readonly ProcedureTrial[],
  planned: PresentedOrderSetOrderDto["plannedTrials"] = [],
) {
  const plan = (planned ?? []).map((trial) => ({
    id: asPlannedTrialId(trial.id),
    values: orderedTrialValues({ parameterValues: trial.values }).map((value) => value.answer),
  }));
  const links = fulfilledPlannedTrials(plan, recorded);
  const performed = new Set<string>(links.values());
  return {
    links,
    /** Planned trials with at least one live trial linked to them. */
    performed,
    outstanding: plan.flatMap((trial, position) => (performed.has(trial.id) ? [] : [position])),
    count: performed.size,
  };
}

export function sortTrials(
  recorded: readonly ProcedureTrial[],
  planned: PresentedOrderSetOrderDto["plannedTrials"] = [],
): ProcedureTrial[] {
  const position = (trial: ProcedureTrial) => {
    const index = planned?.findIndex((plan) => plan.id === trial.plannedTrialId) ?? -1;
    return index < 0 ? (planned?.length ?? 0) : index;
  };
  return [...recorded].sort(
    (a, b) => Date.parse(a.effectiveDateTime) - Date.parse(b.effectiveDateTime) || position(a) - position(b),
  );
}

/** Names each trial by the parameters that differ between trials; the shared ones say nothing. */
export function trialLabels(
  trials: readonly Pick<ProcedureTrial, "id" | "values">[],
  parameters: readonly PresentedOrderSetParameterDto[],
): Map<string, string> {
  const shown = (trial: (typeof trials)[number]) =>
    parameters.map((parameter) => {
      const value = trial.values.find((entry) => trialParameterKey(entry.answer) === trialParameterKey(parameter));
      return value ? displayTrialAnswer(value.answer, parameters) : "";
    });
  const rows = trials.map(shown);
  const varying = parameters.map((_, column) => new Set(rows.map((row) => row[column])).size > 1);
  return new Map(
    trials.map((trial, index) => [
      trial.id,
      (rows[index] ?? []).filter((value, column) => value && (varying[column] || trials.length === 1)).join(" / "),
    ]),
  );
}

/** Current trials in chart order, each named as the per-trial readings card names it ("2 Eyes closed / Foam"). */
export function trialNames(
  recorded: readonly ProcedureTrial[],
  planned: PresentedOrderSetOrderDto["plannedTrials"],
  parameters: readonly PresentedOrderSetParameterDto[],
): Map<string, string> {
  const trials = sortTrials(recorded, planned).filter((trial) => trial.lifecycle.status !== "EnteredInError");
  const labels = trialLabels(trials, parameters);
  return new Map(trials.map((trial, index) => [trial.id, `${index + 1} ${labels.get(trial.id) ?? ""}`.trim()]));
}

/**
 * Procedure-level readings first, in their own order, then trial-level ones in
 * the order of `names`, each carrying its trial's name.
 */
export function inTrialOrder<T extends { trialId?: string; definitionId: string }>(
  readings: readonly T[],
  names: ReadonlyMap<string, string>,
  observationOrder: readonly string[],
): { reading: T; trial?: string }[] {
  const order = [...names.keys()];
  const position = (reading: T) => {
    if (!reading.trialId) return -1;
    const index = order.indexOf(reading.trialId);
    return index === -1 ? order.length : index;
  };
  const rank = (reading: T) => {
    if (!reading.trialId) return 0;
    const index = observationOrder.indexOf(reading.definitionId);
    return index === -1 ? observationOrder.length : index;
  };
  return [...readings]
    .sort((a, b) => position(a) - position(b) || rank(a) - rank(b))
    .map((reading) => {
      const trial = reading.trialId ? names.get(reading.trialId) : undefined;
      return trial ? { reading, trial } : { reading };
    });
}
